import createStore from "zustand";
import { useSession } from "src/components/session/session";
import { useEffect, useCallback } from "react";

import { gql } from "@apollo/client";
import { apolloCore } from "src/utils/apollo/apollo";
import {
  CreateUserCheckoutSessionMutation,
  CreateUserCheckoutSessionMutationVariables,
  CreateUserCheckoutSessionDocument,
  CreateTeamCheckoutSessionMutation,
  CreateTeamCheckoutSessionMutationVariables,
  CreateTeamCheckoutSessionDocument,
} from "src/gql/codegen/graphql";
import * as Sentry from "@sentry/react";

import type { Session } from "src/utils/users/types";
import { useRouter } from "next/router";

type Refetch = () => void;

export type UseCheckoutSessionOutput = readonly [StoreState, Refetch];

export function useTeamCheckoutSession(
  teamId: number
): UseCheckoutSessionOutput {
  const { asPath: redirectPath } = useRouter();
  const session = useSession();

  // Make initial attempt at fetching automatically
  useEffect(() => {
    if (!session) {
      return;
    }

    const state = store.getState();
    const notYetFetched =
      state.status === "init" || state.redirectPath !== redirectPath;

    if (notYetFetched) {
      fetchTeamCheckoutSession(session, teamId, redirectPath);
    }
  }, [session, redirectPath]);

  const refetch = useCallback(() => {
    if (session) {
      fetchTeamCheckoutSession(session, teamId, redirectPath);
    }
  }, [session, redirectPath]);

  return [useStore(), refetch] as const;
}

export function useUserCheckoutSession(): UseCheckoutSessionOutput {
  const { asPath: redirectPath } = useRouter();
  const session = useSession();

  // Make initial attempt at fetching automatically
  useEffect(() => {
    if (!session) {
      return;
    }

    const state = store.getState();
    const notYetFetched =
      state.status === "init" || state.redirectPath !== redirectPath;

    if (notYetFetched) {
      fetchUserCheckoutSession(session, redirectPath);
    }
  }, [session, redirectPath]);

  const refetch = useCallback(() => {
    if (session) {
      fetchUserCheckoutSession(session, redirectPath);
    }
  }, [session, redirectPath]);

  return [useStore(), refetch] as const;
}

// Keep the state in a store so we can use this hook from multiple
// components without fetching more than once
type StoreState =
  | {
      status: "init" | "loading" | "error";
      redirectPath?: string;
      checkoutSessionId?: string;
    }
  | {
      status: "success";
      redirectPath: string;
      checkoutSessionId: string;
    };

const store = createStore<StoreState>(() => ({ status: "init" }));
const useStore = store;

/*
 * Fetch the checkout session through a GraphQL mutation, and update
 * the store state along the way.
 *
 * This way you don't have to handle the side effects of fetching, when
 * calling this function.
 */

async function fetchUserCheckoutSession(
  session: Session,
  redirectPath: string
) {
  // Prevent multiple simultaneous calls
  if (store.getState().status === "loading") {
    return;
  }

  store.setState({ status: "loading", redirectPath });

  apolloCore
    .mutate<
      CreateUserCheckoutSessionMutation,
      CreateUserCheckoutSessionMutationVariables
    >({
      mutation: CreateUserCheckoutSessionDocument,
      variables: { redirect_path: redirectPath },
      context: { session },
    })
    .then((result) => {
      if (!result.data) {
        throw Error("CreateUserCheckoutSession returned no data");
      } else {
        return result.data.create_user_checkout_session.checkout_session_id;
      }
    })
    .then(
      (checkoutSessionId) => {
        store.setState({ status: "success", checkoutSessionId });
      },
      (error) => {
        store.setState({ status: "error" });
        Sentry.captureException(error);
      }
    );
}

async function fetchTeamCheckoutSession(
  session: Session,
  teamId: number,
  redirectPath: string
) {
  // Prevent multiple simultaneous calls
  if (store.getState().status === "loading") {
    return;
  }

  store.setState({ status: "loading", redirectPath });

  apolloCore
    .mutate<
      CreateTeamCheckoutSessionMutation,
      CreateTeamCheckoutSessionMutationVariables
    >({
      mutation: CreateTeamCheckoutSessionDocument,
      variables: { team_id: teamId, redirect_path: redirectPath },
      context: { session },
    })
    .then((result) => {
      if (!result.data) {
        throw Error("CreateTeamCheckoutSession returned no data");
      } else {
        return result.data.create_team_checkout_session.checkout_session_id;
      }
    })
    .then(
      (checkoutSessionId) => {
        store.setState({ status: "success", checkoutSessionId });
      },
      (error) => {
        store.setState({ status: "error" });
        Sentry.captureException(error);
      }
    );
}

gql`
  mutation CreateUserCheckoutSession($redirect_path: String!) {
    create_user_checkout_session(redirect_path: $redirect_path) {
      checkout_session_id
    }
  }
`;

gql`
  mutation CreateTeamCheckoutSession($team_id: Int!, $redirect_path: String!) {
    create_team_checkout_session(
      team_id: $team_id
      redirect_path: $redirect_path
    ) {
      checkout_session_id
    }
  }
`;
